import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

function readCookie(name: string) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : "";
}

// ---------------------------------------------------------------------------
// In-memory CSRF token store
//
// In a cross-origin deployment (frontend on domain A, backend on domain B),
// the backend's Set-Cookie response stores `csrf_token` under the backend
// domain. JavaScript running on the frontend domain cannot read it via
// `document.cookie` (which only exposes cookies for the current domain).
// Instead, callers capture the CSRF token from JSON response bodies (login,
// mfa/verify, GET /auth/csrf all return it) and store it here so the request
// interceptor can echo it in X-CSRF-Token regardless of origin.
//
// Same-origin setups still benefit: `document.cookie` is tried first and this
// store is used only as a fallback, preserving backward compatibility.
// ---------------------------------------------------------------------------
let _csrfToken = "";
let _supportSessionId = "";

/** Set or clear the active audited support-access session for tenant-scoped requests. */
export function setActiveSupportSession(sessionId: string): void {
  _supportSessionId = sessionId || "";
}

/** Store the CSRF token in memory so it can be echoed in X-CSRF-Token even
 *  when `document.cookie` is not accessible (cross-origin deployments). */
export function setInMemoryCsrf(token: string): void {
  if (token) _csrfToken = token;
}

/** Read the current CSRF token. A token captured from the latest auth/CSRF
 *  response is authoritative. The cookie remains a fallback for same-origin
 *  sessions that have not yet populated the in-memory store. */
function readCsrfToken(): string {
  return _csrfToken || readCookie("csrf_token");
}

/** Test-only: reset in-memory CSRF state between test cases. */
export function _resetCsrfForTests(): void {
  _csrfToken = "";
  _supportSessionId = "";
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

/**
 * Rotate and synchronize CSRF immediately before a security-sensitive
 * mutation. The response body is authoritative for cross-origin deployments,
 * where frontend JavaScript cannot read the backend-domain cookie.
 */
export async function refreshCsrfToken(): Promise<string> {
  const response = await api.get("/auth/csrf");
  const token = response.data?.csrf_token || "";
  if (!token) throw new Error("csrf_refresh_failed");
  setInMemoryCsrf(token);
  return token;
}

api.interceptors.request.use((cfg) => {
  if (_supportSessionId) cfg.headers["X-Support-Session-Id"] = _supportSessionId;
  const method = (cfg.method || "get").toUpperCase();
  if (["POST", "PATCH", "PUT", "DELETE"].includes(method)) {
    const csrf = readCsrfToken();
    if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  }
  return cfg;
});

// -------- Session-expiry singleton --------
// Ensures we clear client state, show a SINGLE toast, and redirect exactly
// once even if many in-flight requests all 401 concurrently.
let hasBeenAuthenticated = false;   // Flipped true by AuthContext after /auth/me OK
let sessionExpiryTriggered = false;

/** Called by AuthContext once a `/auth/me` returns a valid user. Enables the
 *  session-expiry flow — without this signal we treat 401s as anonymous probes
 *  (which happen on every initial page load) and stay silent. */
export function markAuthenticated() {
  hasBeenAuthenticated = true;
}
/** Called on explicit logout. Prevents the "session expired" toast from
 *  firing when the user chose to sign out themselves. */
export function markSignedOut() {
  hasBeenAuthenticated = false;
  sessionExpiryTriggered = false;
}
/** Test-only helper — resets the module state between vitest cases. */
export function _resetSessionExpiryForTests() {
  hasBeenAuthenticated = false;
  sessionExpiryTriggered = false;
}

function triggerSessionExpiry() {
  if (sessionExpiryTriggered) return;
  sessionExpiryTriggered = true;
  // Guard against jsdom teardown / non-browser environments where the
  // global `window` binding is torn down before this callback fires
  // (e.g. Vitest tests that exercise the 401 path). In production
  // browser environments this guard is a no-op.
  if (typeof window === "undefined") return;
  try {
    toast.dismiss();
    toast.error("Your session has expired. Please sign in again.");
  } catch { /* toaster may not be mounted */ }
  try { window.dispatchEvent(new CustomEvent("rk:session-expired")); } catch { /* noop */ }
  const dest = (window.location.pathname || "/") + (window.location.search || "");
  const alreadyOnLogin = window.location.pathname.startsWith("/login");
  if (!alreadyOnLogin) {
    setTimeout(() => {
      if (typeof window === "undefined") return;
      window.location.replace(`/login?redirect=${encodeURIComponent(dest)}`);
    }, 50);
  }
}

/** Public helper so components can suppress their local error UI when the
 *  interceptor has already surfaced a session-expiry toast + redirect. */
export function isSessionExpired(err: unknown): boolean {
  return typeof err === "object" && err !== null && (err as any).sessionExpired === true;
}

// -------- Auto-refresh + session-expiry response interceptor --------
let refreshing: Promise<any> | null = null;
let csrfRefreshing: Promise<string> | null = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isAuthMiss = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/csrf");

    // In cross-origin deployments the browser cannot read the backend's CSRF
    // cookie. Refresh the token from JSON and replay a failed mutation once.
    // Axios retains the original request body, including FormData uploads.
    if (
      status === 403 &&
      err?.response?.data?.detail === "csrf_check_failed" &&
      err.config &&
      !err.config._csrfRetry &&
      !isAuthMiss
    ) {
      err.config._csrfRetry = true;
      try {
        csrfRefreshing = csrfRefreshing || api.get("/auth/csrf").then((response) => {
          const token = response.data?.csrf_token || "";
          if (!token) throw new Error("csrf_refresh_failed");
          setInMemoryCsrf(token);
          return token;
        });
        await csrfRefreshing;
        csrfRefreshing = null;
        if (err.config.headers) {
          delete err.config.headers["X-CSRF-Token"];
          delete err.config.headers["x-csrf-token"];
        }
        return api(err.config);
      } catch {
        csrfRefreshing = null;
      }
    }

    if (status === 401 && !err.config?._retry && !isAuthMiss) {
      err.config._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        // Strip stale csrf so the request interceptor re-reads the fresh cookie
        if (err.config.headers) {
          delete err.config.headers["X-CSRF-Token"];
          delete err.config.headers["x-csrf-token"];
        }
        return api(err.config);
      } catch {
        refreshing = null;
        // Refresh failed. Only surface the "session expired" flow if the user
        // was previously authenticated (i.e. AuthContext saw a valid /auth/me
        // in this browsing session). Otherwise it's an anonymous probe — stay
        // silent and let the login page handle it.
        if (hasBeenAuthenticated) {
          triggerSessionExpiry();
          (err as any).sessionExpired = true;
        }
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail: unknown): string {
  if (detail == null) return "Something went wrong.";
  if (detail === "too_many_attempts") return "Too many unsuccessful sign-in attempts. Please wait 15 minutes before trying again.";
  if (detail === "not_found") return "This record could not be found or is no longer available to your account. Refresh and try again.";
  if (detail === "contact_not_found_or_inaccessible") return "This contact is no longer available to your account. Refresh before trying again.";
  if (detail === "organization_not_found") return "This organization could not be found. Refresh the list and try again.";
  if (detail === "organization_pending_territory_review") return "This organization must be approved for its territory before it can be linked.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e: any) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  if (typeof detail === "object" && typeof (detail as any).msg === "string") return (detail as any).msg;
  if (typeof detail === "object" && (detail as any).code === "required_workflow_fields_missing") {
    const fields = (detail as any).missing_fields || (detail as any).fields || [];
    const labels = Array.isArray(fields)
      ? fields.map((field: any) => String(field?.label || field?.field || field).replaceAll("_", " "))
      : [];
    return labels.length
      ? `Complete the required fields: ${labels.join(", ")}.`
      : "Complete all required workflow fields before saving.";
  }
  if (typeof detail === "object" && (detail as any).code) return String((detail as any).code).replaceAll("_", " ");
  return JSON.stringify(detail);
}

/** Consolidated toast helper: renders an error toast unless the underlying
 *  failure is a session-expiry (in which case the interceptor already surfaced
 *  a single "Your session has expired…" toast + redirect). Use this in every
 *  component that used to call `toast.error(formatApiError(e.response?.data?.detail))`. */
export function toastError(err: unknown, fallback?: string) {
  if (isSessionExpired(err)) return;
  const detail = (err as any)?.response?.data?.detail;
  const msg = formatApiError(detail) || fallback || (err as any)?.message || "Something went wrong.";
  const requestId = (err as any)?.response?.headers?.["x-request-id"];
  const display = requestId ? `${msg} Reference: ${requestId}` : msg;
  try { toast.error(display); } catch { /* toaster may not be mounted */ }
}
